import { useCallback, useEffect, useMemo, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import StorefrontIcon from "@mui/icons-material/Storefront";
import {
  getPurchaseOrderCustomerCandidates,
  setPurchaseOrderCustomer,
} from "../../services";
import {
  PurchaseOrderCustomerCandidate,
  PurchaseOrderCustomerCandidatesData,
  PurchaseOrderListItem,
} from "../../types/purchase-order.type";
import { useDebounce } from "../../utils/hooks/useDebounce";
import {
  PO_COMPACT_MEDIA,
  PO_EMPTY_TEXT,
  PO_SEARCH_DEBOUNCE_MS,
} from "./data";

/** หัวข้อของสามกลุ่มใน dropdown ตามลำดับที่ต้องเสนอ */
const GROUP_LABEL = {
  storeNumber: "store number ตรงกัน",
  nameMatch: "ชื่อคล้าย",
  search: "ค้นหาเอง",
};

interface CandidateOption {
  readonly customer_uuid: string;
  readonly label: string;
  readonly group: string;
}

const toOption = (
  candidate: PurchaseOrderCustomerCandidate,
  group: string,
  suffix?: string,
): CandidateOption => ({
  customer_uuid: candidate.customer_uuid,
  label:
    [candidate.customer_code, candidate.customer_name]
      .filter(Boolean)
      .join(" · ") + (suffix ?? ""),
  group,
});

interface CustomerMatchPanelProps {
  purchaseOrder: PurchaseOrderListItem;
  /** เพิ่มค่าเมื่อมีร้านใหม่ถูกเปิด เพื่อให้ตัวเลือกชุดเดิมถูกทิ้งและอ่านใหม่ */
  refreshToken: number;
  onMatched: (row: PurchaseOrderListItem) => void;
  /** ไม่ส่ง = ไม่มีปุ่มเปิดลูกค้าใหม่ (drawer ของ HO) */
  onOpenNewCustomer?: () => void;
  /** HO เท่านั้น: เพิ่มปุ่ม "บันทึกและส่งสาขา" (publish ในคำขอเดียวกับการจับคู่) */
  allowPublish?: boolean;
}

/**
 * แผงจับคู่ร้านของ PO หนึ่งใบใน W7
 *
 * ค้นหาและเลือกอยู่ในช่องเดียวกัน — พิมพ์เพื่อค้นร้านในสาขา และตัวเลือกที่ขึ้นมาถูกคั่น
 * ด้วยหัวข้อสามกลุ่มตามลำดับที่เอกสารกำหนด จึงเห็นได้ว่าร้านที่เสนอมาจากเกณฑ์ไหน
 *
 * ต้องมีคนเลือกและกดบันทึกทุกใบ — ไม่มีการเลือกให้อัตโนมัติแม้จะมีร้านที่ store number
 * ตรงกันเพียงร้านเดียว · ตัวเลือกถูกอ่านตอนเปิดครั้งแรกเท่านั้น เพราะหนึ่งหน้าอาจมีใบที่
 * ค้างอยู่ถึง 100 ใบ การอ่านทุกแผงพร้อมกันจะยิงคำขอพร้อมกันทั้งหน้า
 */
export default function CustomerMatchPanel({
  purchaseOrder,
  refreshToken,
  onMatched,
  onOpenNewCustomer,
  allowPublish = false,
}: CustomerMatchPanelProps) {
  const [candidates, setCandidates] =
    useState<PurchaseOrderCustomerCandidatesData | null>(null);
  const [opened, setOpened] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  // ปุ่มที่กำลังส่งคำขอ — หมุนเฉพาะปุ่มนั้น และล็อกอีกปุ่มระหว่างรอ
  const [savingMode, setSavingMode] = useState<"save" | "publish" | null>(
    null,
  );
  const [search, setSearch] = useState<string>("");
  const [selected, setSelected] = useState<CandidateOption | null>(null);

  const debouncedSearch = useDebounce(search, PO_SEARCH_DEBOUNCE_MS);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getPurchaseOrderCustomerCandidates(
        purchaseOrder.uuid,
        debouncedSearch || undefined,
      );
      setCandidates(data);
    } catch {
      // 403 (PO ของสาขาอื่น) และความผิดพลาดอื่นถูกแจ้งโดย interceptor แล้ว
    } finally {
      setLoading(false);
    }
  }, [purchaseOrder.uuid, debouncedSearch]);

  useEffect(() => {
    if (!opened) return;
    loadCandidates();
  }, [opened, loadCandidates]);

  // ร้านที่เพิ่งเปิดใหม่ยังไม่อยู่ในชุดตัวเลือกที่อ่านไว้ ทิ้งของเดิมและล้างสิ่งที่เลือกค้างไว้
  // เพราะรายชื่อเปลี่ยนแล้ว คนจับคู่ต้องเลือกใหม่จากชุดที่มีร้านใหม่อยู่ด้วย
  useEffect(() => {
    setCandidates(null);
    setSelected(null);
    setOpened(false);
  }, [refreshToken]);

  const options = useMemo<CandidateOption[]>(() => {
    if (!candidates) return [];
    return [
      ...candidates.store_number_matches.map((candidate) =>
        toOption(candidate, GROUP_LABEL.storeNumber),
      ),
      ...candidates.name_matches.map((candidate) =>
        toOption(
          candidate,
          GROUP_LABEL.nameMatch,
          ` (${candidate.similarity_percent}%)`,
        ),
      ),
      ...candidates.search.map((candidate) =>
        toOption(candidate, GROUP_LABEL.search),
      ),
    ];
  }, [candidates]);

  const handleOpenDropdown = () => {
    setOpened(true);
  };

  const handleChangeSelection = (
    _event: unknown,
    option: CandidateOption | null,
  ) => {
    setSelected(option);
  };

  const handleChangeSearch = (_event: unknown, value: string) => {
    setSearch(value);
  };

  const save = async (publish: boolean) => {
    if (!selected) return;

    setSavingMode(publish ? "publish" : "save");
    try {
      const {
        data: { data },
      } = await setPurchaseOrderCustomer(
        purchaseOrder.uuid,
        selected.customer_uuid,
        publish,
      );
      if (data) onMatched(data);
    } catch {
      // 403 (สาขาอื่น) / 409 / 422 (ร้านผิดเชน หรือ PO ถูกใช้ไปแล้ว) แจ้งโดย interceptor
    } finally {
      setSavingMode(null);
    }
  };

  const handleSave = () => save(false);
  const handleSaveAndPublish = () => save(true);

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      flexWrap="wrap"
      gap={1.5}
      sx={{ [PO_COMPACT_MEDIA]: { gap: 1 } }}
    >
      {/* รหัสร้านในไฟล์คือค่าที่ต้องกรอกในช่อง store number ตอนเปิดลูกค้าใหม่
          กินพื้นที่ที่เหลือทั้งหมด เพื่อดันช่องเลือกร้านและปุ่มไปชิดขวาให้ตรงกันทุกแถว */}
      <Stack sx={{ flex: "1 1 200px", minWidth: 190 }}>
        <Typography variant="caption" color="text.secondary">
          ร้านในไฟล์ · store number {purchaseOrder.ship_to_code}
        </Typography>
        <Typography variant="body2" fontWeight={600}>
          {purchaseOrder.ship_to_name || PO_EMPTY_TEXT}
        </Typography>
      </Stack>

      <Autocomplete
        size="small"
        // ความกว้างคงที่ ไม่ยืดตามพื้นที่ว่าง ทุกแถวจึงกว้างเท่ากันและขอบขวาตรงกัน
        sx={{ width: 360, flexShrink: 0, [PO_COMPACT_MEDIA]: { width: 280 } }}
        options={options}
        groupBy={(option) => option.group}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) =>
          option.customer_uuid === value.customer_uuid
        }
        // ฝั่ง API เป็นคนค้นให้แล้ว การกรองซ้ำในเบราว์เซอร์จะตัดกลุ่ม store number
        // และชื่อคล้ายทิ้งทันทีที่พิมพ์ ทั้งที่สองกลุ่มนั้นไม่ได้มาจากคำค้น
        filterOptions={(option) => option}
        value={selected}
        onChange={handleChangeSelection}
        inputValue={search}
        onInputChange={handleChangeSearch}
        onOpen={handleOpenDropdown}
        loading={loading}
        loadingText="กำลังค้นหาร้าน..."
        noOptionsText="ไม่พบร้านที่ตรง ลองค้นหาด้วยชื่อหรือรหัส"
        renderInput={(params) => (
          <TextField
            {...params}
            label="เลือกร้าน"
            placeholder="พิมพ์ชื่อหรือรหัสร้านเพื่อค้นในสาขา"
          />
        )}
      />

      <Button
        variant={allowPublish ? "outlined" : "contained"}
        loading={savingMode === "save"}
        disabled={!selected || savingMode !== null}
        onClick={handleSave}
        sx={{ flexShrink: 0 }}
      >
        บันทึก
      </Button>

      {allowPublish && (
        <Button
          variant="contained"
          loading={savingMode === "publish"}
          disabled={!selected || savingMode !== null}
          onClick={handleSaveAndPublish}
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          บันทึกและส่งสาขา
        </Button>
      )}

      {onOpenNewCustomer && (
        <Button
          variant="text"
          startIcon={<StorefrontIcon />}
          onClick={onOpenNewCustomer}
          sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
        >
          เปิดลูกค้าใหม่
        </Button>
      )}
    </Stack>
  );
}
